import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Components
import {
  AddMobileShoutout,
  Clickable,
  Col,
  ComingSoon,
  Icon,
  Modal,
  Post as PostComponent,
  Row,
  Sidepanel,
  Spinner2,
  Text
} from 'components';
import SignUp from 'screens/SignUp';
import { PaymentModal } from 'screens/Subscribe/screens/UserList/components';
import { Fan, Header, SharedShoutout } from './components';
import CallToActionModal from './components/CallToActionModal';

// Utils
import { getCalculatedText, isDeviceIOS } from 'core';
import { Api, theme } from 'lib';

/*
  `fancam` is the FanCam Challenge section's address, which a campaign email's "Join the Challenge"
  now carries (`/profile/:team/fancam`). On the web the section is app-only, so it is the same
  handoff `fancam-challenge` already gives — aliased here rather than listed twice.
*/
const CTA_ALIASES = { fancam: 'fancamchallenge' };

const normalizeCTA = (value) => {
  const normalized = value?.toLowerCase().replace(/[-_]/g, '');
  return CTA_ALIASES[normalized] ?? normalized;
};

const Profile = ({ userId }) => {
  const [otherUser, setOtherUser] = useState(null),
    // [segmentNum, setSegmentNum] = useState(0),
    [showSignup, setShowSignup] = useState(false),
    [loading, setLoading] = useState(true),
    [isCurrentUser, setIsCurrentUser] = useState(null),
    currentUser = useSelector((state) => state.user),
    isMobile = useSelector((state) => state.appState.isMobile),
    { t } = useTranslation(),
    dispatch = useDispatch(),
    navigate = useNavigate(),
    location = useLocation();

  const { username, cta, uuid } = useParams();
  const [isCTAModalVisible, setIsCTAModalVisible] = useState(false);
  const [isSkipToSignUp, setIsSkipToSignUp] = useState(false);
  const [actionType, setActionType] = useState(cta);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [isComingSoonVisible, setIsComingSoonVisible] = useState(false);
  const [shoutoutSelection, setShoutoutSelection] = useState(null);
  const [isPostModalVisible, setIsPostModalVisible] = useState(
    (cta?.toLowerCase() === 'post' || cta?.toLowerCase() === 'shoutout') && uuid
  );
  const queryParams = new URLSearchParams(location.search);
  const isPaymentRequired = queryParams.get('isPaymentRequired');

  const cards = currentUser?.billing?.cards ?? [];

  let user = isCurrentUser ? currentUser : otherUser,
    isFan = user?.type === 'fan',
    isAthlete = user?.type === 'athlete',
    isTeam = user?.type === 'team',
    // TODO: old logic — checked Redux subscriptions store
    isSubscribed = useSelector((state) =>
      state.subscriptions?.find(
        (x) => x.subscribed._id === otherUser?._id && x.type !== 'bundle' && x.status === 'active'
      )
    ),
    contentLocked = isAthlete && currentUser?.type === 'fan' && !isSubscribed;

  const isFollowing = currentUser?.following?.includes(otherUser?._id) ?? false;
  const isLoggedIn = currentUser !== null;

  // const options = [
  //   {
  //     title: t('profile.recentUpdates'),
  //     component: <Updates
  //       data={
  //         [...(user?.updates || [])].sort((a, b) =>
  //           new Date(b.date) - new Date(a.date)
  //         )
  //       }
  //     />
  //   },
  //   {
  //     title: t('common.shoutouts'),
  //     component: <ShoutoutsFeed user={user} setUser={setOtherUser} />
  //   },
  //   {
  //     title: t('common.posts'),
  //     component: <PostsGrid marg='24px 0 0' haveBackgroundStrip data={user?.posts} title={'Posts'} />
  //   },
  //   {
  //     title: t('profile.myEquipment'),
  //     component: (
  //       <ItemList
  //         marg={isMobile ? '16px 0 8px' : '0 0 32px'}
  //         title={t('profile.myEquipment')}
  //         limitless={!isMobile}
  //         data={user?.products}
  //       />
  //     )
  //   },
  //   // {
  //   //   title: t('profile.trendingPosts'),
  //   //   component: (
  //   //     <PostsGrid
  //   //       marg='24px 0 0'
  //   //       data={[...(user?.posts || [])].sort((a, b) => b.reactionCount - a.reactionCount)}
  //   //       title={'Trending posts'}
  //   //     />
  //   //   )
  //   // },
  //   {
  //     title: t('common.stats'),
  //     component: <Stats />
  //   }
  // ]

  useEffect(() => {
    setOtherUser(null);
    fetchOtherUser();

    // remove automatically showing of signup modal
    // if(!currentUser) {
    //   setTimeout(() => (
    //     setShowSignup(true)
    //   ), 10000)
    // }
  }, [username]);

  useEffect(() => {
    setOtherUser(null);
    if (!userId) return;

    fetchOtherUser();
  }, [userId]);

  useEffect(() => {
    const normalizedCTA = normalizeCTA(cta);
    const isFollowCTA = normalizedCTA === 'follow';
    const isSubscribeCTA = normalizedCTA === 'subscribe';
    const isFace2FaceCTA = normalizedCTA === 'face2face';
    const isAdPlayazCTA = normalizedCTA === 'adplayaz';
    const isRequestShoutoutCTA = normalizedCTA === 'requestshoutout';
    const isSendShoutoutCTA = normalizedCTA === 'sendshoutout';
    const isAppOnlyCTA = [
      'fancamlive',
      'fancamchallenge',
      'playtowinpoll',
      'playtowinquickquiz',
      'predictscore',
      /*
        Rewards, where a campaign email's "See Rewards" button now lands.

        The section is the app's — a carousel inside the team channel — so the web answer is the
        handoff the other app-only sections already give, on the team's own page. Without this the
        address still resolved, to the top of the team profile with the segment ignored, which is
        the defect QA reported rather than a fix for it.
      */
      'rewards'
    ].includes(normalizedCTA);

    const shouldDisplayFollowModal = isFollowCTA && !isFollowing;
    const shouldDisplaySubscribeModal = isSubscribeCTA && !isSubscribed;

    // if ((isSendShoutoutCTA || isRequestShoutoutCTA) && isLoggedIn && cards.length === 0) {
    //   // require payment method
    //   setPaymentModalVisible(true);
    //   return;
    // }

    if (isAdPlayazCTA || isFace2FaceCTA) {
      setIsComingSoonVisible(true);
      return;
    }

    if (isRequestShoutoutCTA && isLoggedIn) {
      setShoutoutSelection({ preSelect: 0, preSelectPage: 'shoutout' });
      return;
    }

    if (isSendShoutoutCTA) {
      navigate(`/shoutout/${username}`, { replace: true });
      return;
    }

    const shouldShowCTAModal =
      shouldDisplayFollowModal ||
      shouldDisplaySubscribeModal ||
      isRequestShoutoutCTA ||
      isAppOnlyCTA;

    setIsCTAModalVisible(shouldShowCTAModal);
  }, [cta, isFollowing, isSubscribed, uuid, isLoggedIn]);

  function handleClosePaymentAndCTAModals() {
    // hack: force close the CTA modal. without this, the isFollowing (dependent user, dispatch above) will be updated, calling the useEffect in the parent component which displays the modal again
    setIsCTAModalVisible(false);
    setPaymentModalVisible(false);
    setShoutoutSelection(null);
  }

  // silent skips the loading spinner — used for background refreshes (e.g. after
  // a shout-out is posted) where swapping the page for a spinner would be jarring
  async function fetchOtherUser({ silent } = {}) {
    if (!silent) setLoading(true);

    let res;
    if (!currentUser && username) {
      res = await Api.get(`/users/getByUsername/${username}`);
    } else {
      res = await Api.post(`/users/get`, { userId, username });
      res = res?.user;
    }

    if (!res) {
      if (!silent) setLoading(false);
      return;
    }

    // Add read more previews to the captions
    const userPosts = await Promise.all(
      (res.posts || []).map(async (x) => ({
        ...x,
        calculatedText: await getCalculatedText(x.caption)
      }))
    );

    res.posts = userPosts;

    dispatch({
      type: 'ADD_POSTS',
      payload: res.posts
    });

    setOtherUser(res);
    setIsCurrentUser(currentUser && res._id === currentUser._id);
    if (!silent) setLoading(false);
  }

  // the shout-out lands on the channel feed, which comes from the profile
  // payload — pull it again so the new one shows without a manual refresh
  function handleShoutoutPosted() {
    fetchOtherUser({ silent: true });
  }

  const handleUpdateProfileViewsCount = () => {
    setOtherUser((prev) => ({
      ...prev,
      profileViewsCount: prev.profileViewsCount + 1
    }));
  };

  if (loading)
    return (
      <Col wid="100%" ht="calc(100vh - 74px)" noFlex centerAll>
        <Spinner2 col={theme.SECONDARY} />
      </Col>
    );

  if (!user)
    return (
      <Col centerAll>
        <Text h3 marg="16px 0 0" col={theme.GREY_60}>
          {t('profile.userNotFound', { username })}
        </Text>
      </Col>
    );

  return (
    <React.Fragment>
      {/* POST PREVIEW */}
      <Modal
        wid={isMobile ? '90vw !important' : '936px !important'}
        visible={isPostModalVisible}
        style={{
          alignItems: !isMobile ? 'center' : isDeviceIOS() ? 'flex-start' : 'center',
          paddingTop: isMobile && isDeviceIOS() ? '60px' : 0
        }}
        closeModal={() => {
          setIsPostModalVisible(false);
        }}
      >
        {cta?.toLowerCase() === 'post' ? (
          <>
            <PostComponent
              item={{ _id: uuid }}
              allowCommenting={false}
              handleClose={() => setIsPostModalVisible(false)}
              onHandleUpdateProfileViewsCount={handleUpdateProfileViewsCount}
            />
          </>
        ) : (
          <>
            <SharedShoutout
              user={user}
              shoutoutId={uuid}
              handleClose={() => setIsPostModalVisible(false)}
            />
          </>
        )}
      </Modal>

      <Sidepanel
        fullScreen
        visible={showSignup}
        handleClose={() => setShowSignup(false)}
        ignoreHeader
      >
        <SignUp isSidebar />

        <Col pos="absolute" top="40px" right="40px">
          <Clickable onClick={() => setShowSignup(false)}>
            <Icon type="close" dimensions={20} col={theme.GREY_60} />
          </Clickable>
        </Col>
      </Sidepanel>

      <Col wid="100%" centerAll bg={currentUser ? '#fff' : '#F3F5F8'} start>
        {isMobile && !isCurrentUser && currentUser && (
          <Col
            ht="38px"
            wid="100%"
            noFlex
            borderBottom={`2px solid ${theme.GREY_20}`}
            bg="#fff"
            pos="fixed"
            top={0}
          >
            <Row between pad="0 16px">
              <Row ht="100%" center>
                <Clickable onClick={() => navigate(-1)}>
                  <Icon type="arrowLeft" dimensions={16} />
                </Clickable>
              </Row>

              <Row>
                <Text b2 bold>
                  {user.name}
                </Text>
              </Row>

              <Row wid="16px" />
            </Row>
          </Col>
        )}

        <Row noFlex wid="100%" start>
          <Col wid="100%" marg={isLoggedIn && isMobile && !isCurrentUser && '38px 0 0'}>
            <Row>
              <Header
                user={user}
                isCurrentUser={isCurrentUser}
                updateOtherUser={setOtherUser}
                currentUser={currentUser}
                setIsCTAModalVisible={setIsCTAModalVisible}
                isLoggedIn={isLoggedIn}
                setIsSkipToSignUp={setIsSkipToSignUp}
                setActionType={setActionType}
                setPaymentModalVisible={setPaymentModalVisible}
                onHandleClosePaymentAndCTAModals={handleClosePaymentAndCTAModals}
                onShoutoutPosted={handleShoutoutPosted}
              />
            </Row>

            <Col
              center
              wid={isMobile ? '100vw' : '936px'}
              marg={isMobile ? '24px 0 120px 0' : '44px 0 84px 0'}
            >
              {loading ? (
                <Col wid="100%" noFlex centerAll>
                  <Spinner2 col={theme.SECONDARY} />
                </Col>
              ) : (
                <Fan
                  user={user}
                  contentLocked={contentLocked}
                  otherUser={otherUser}
                  setOtherUser={setOtherUser}
                />
              )}
            </Col>
          </Col>
        </Row>
      </Col>

      <CallToActionModal
        isVisible={isCTAModalVisible}
        onCloseModal={() => setIsCTAModalVisible(false)}
        otherUser={otherUser}
        setOtherUser={setOtherUser}
        cta={actionType}
        isFollowing={isFollowing}
        isSkipToSignUp={isSkipToSignUp}
        setShoutoutSelection={setShoutoutSelection}
        isPaymentRequired={isLoggedIn && cards.length === 0}
        setPaymentModalVisible={setPaymentModalVisible}
        deeplinkPath={location.pathname}
      />

      <PaymentModal
        visible={paymentModalVisible}
        closeModal={() => setPaymentModalVisible(false)}
        navigate={navigate}
        athleteId={otherUser?._id}
        athleteName={user.username}
        cta={actionType}
      />

      <AddMobileShoutout
        userData={otherUser}
        visible={shoutoutSelection !== null}
        setVisible={() => setShoutoutSelection(null)}
        preSelectData={{ entity: otherUser }}
        preSelectPage={shoutoutSelection?.preSelectPage}
        preSelect={shoutoutSelection?.preSelect}
        onShoutoutPosted={handleShoutoutPosted}
      />

      <ComingSoon
        visible={isComingSoonVisible}
        closeModal={() => setIsComingSoonVisible(false)}
        headerText={t(`common.${cta}`)}
      />
    </React.Fragment>
  );
};

export default Profile;

/**
 * Code for desktop profile screen with tab
 *
 */
// {!isMobile ? (
//   <React.Fragment>
//     {isCurrentUser
//       ? (
//         <Col wid='100%' bg='#fff'>
//           <Col marg='35px 0 0' wid='100%'>
//             <Shoutouts user={user}/>
//           </Col>

//           <Col marg='40px 0 0'>
//             <PostsGrid
//               data={user.posts}
//               title={
//                 isCurrentUser
//                   ? t('home.myPosts')
//                   : t('profile.trendingPosts')
//               }
//             />
//           </Col>
//         </Col>
//       ) : (
//         <Col wid='100%'>
//           <Col marg='51px 0 0' wid='100%'>
//             <TabControl
//               options={options}
//               selectedIdx={segmentNum}
//               handleSelect={setSegmentNum}
//             />
//           </Col>

//           <Col marg='32px 0 0'>
//             {options[segmentNum].component}
//           </Col>
//         </Col>
//       )
//     }
//   </React.Fragment>
// ) : (
//   <Col bg='#fff' wid='100%'>
//     {options.map(x => (
//       <Col wid='100%'>
//         {x.component}
//       </Col>
//     ))}
//   </Col>
// )}
